import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

export const connectYouTube = async () => {
  try { const { data } = await api.post("/admin/youtube/connect"); window.location.href = data.auth_url; } catch (e) { toast.error(errMsg(e)); }
};

export const YouTubeWarningBanner = () => {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/youtube/status").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s || (s.connected && s.mode === "live" && s.health?.ok !== false)) return null;
  const broken = s.connected && s.health?.ok === false;
  return (
    <div role="alert" className="mt-6 flex flex-col gap-4 rounded-2xl border-4 border-[#991B1B] bg-[#B91C1C] p-6 text-white shadow-lg sm:flex-row sm:items-center sm:justify-between" data-testid="youtube-not-connected-banner">
      <div>
        <p className="font-serif text-3xl font-bold" data-testid="youtube-banner-title">{broken ? "YouTube connection stopped working" : "YouTube not connected"}</p>
        <p className="mt-2 text-lg">{broken
          ? `Google stopped letting the site post to ${s.expected_handle}, so new orders will get stuck at the YouTube step. Reconnect, then retry any failed YouTube jobs.`
          : `Videos are NOT being posted to ${s.expected_handle}. New orders will only get a simulated (MOCK) upload until you connect the channel.`}</p>
      </div>
      <button className="shrink-0 rounded-xl bg-white px-6 py-4 text-lg font-bold text-[#991B1B] transition-transform hover:scale-[1.03] disabled:opacity-60" onClick={connectYouTube} disabled={!s.credentials_configured} data-testid="youtube-banner-connect-button">{broken ? "Reconnect YouTube now" : "Connect YouTube now"}</button>
    </div>
  );
};

export const AdminYouTube = () => {
  const [s, setS] = useState(null);
  const [params, setParams] = useSearchParams();
  const load = useCallback(() => api.get("/admin/youtube/status").then(({ data }) => setS(data)), []);
  useEffect(() => {
    load();
    const r = params.get("youtube");
    if (r === "connected") toast.success("YouTube channel connected");
    if (r === "error") toast.error(`YouTube connection failed (${params.get("reason")})`);
    if (r) setParams({}, { replace: true });
  }, [load, params, setParams]);
  const connect = connectYouTube;
  const [checking, setChecking] = useState(false);
  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/admin/youtube/check");
      data.ok ? toast.success("YouTube connection is working") : toast.error("YouTube connection stopped working. Please reconnect.");
    } catch (e) { toast.error(errMsg(e)); }
    setChecking(false);
    load();
  };
  const disconnect = async () => {
    if (!window.confirm("Disconnect the YouTube channel? New uploads will fail until you reconnect.")) return;
    await api.post("/admin/youtube/disconnect");
    load();
  };
  if (!s) return null;
  return (
    <div className="paper-card space-y-3 p-5" data-testid="youtube-connection-card">
      <p className="font-serif text-2xl font-bold">YouTube channel</p>
      {s.connected ? (
        <p className="text-lg" data-testid="youtube-connected-channel">
          Connected to <b>{s.channel.title}</b> {s.channel.handle && `(${s.channel.handle})`}
          {!s.handle_matches && <span className="ml-2 font-semibold text-[#991B1B]">This is not {s.expected_handle}. Please reconnect with the right channel.</span>}
        </p>
      ) : (
        <p className="text-lg text-[#5C4A3E]" data-testid="youtube-not-connected">Not connected. Sign in with the Google account that owns {s.expected_handle}.</p>
      )}
      <p className="text-base text-[#5C4A3E]">
        Upload mode: <b data-testid="youtube-mode">{s.mode === "live" ? "LIVE (real uploads)" : "MOCK (simulated, set YOUTUBE_MODE=live to go live)"}</b>
        {!s.credentials_configured && <> · <span className="text-[#991B1B]">Google OAuth client ID/secret not added yet</span></>}
      </p>
      {s.credentials_configured && <p className="break-all text-sm text-[#8C7A6B]">Authorized redirect URI: {s.redirect_uri}</p>}
      {s.connected && s.health?.checked_at && (
        <p className={`text-base font-semibold ${s.health.ok ? "text-[#2E6930]" : "text-[#991B1B]"}`} data-testid="youtube-health-status">
          {s.health.ok ? "Connection working" : "Connection stopped working"} · checked {new Date(s.health.checked_at).toLocaleString()}
          {!s.health.ok && s.health.error && <span className="block text-sm font-normal">{s.health.error}</span>}
        </p>
      )}
      <div className="flex flex-wrap gap-3">
        <button className="btn-primary !min-h-0 !py-2" onClick={connect} disabled={!s.credentials_configured} data-testid="youtube-connect-button">{s.connected ? "Reconnect channel" : "Connect YouTube channel"}</button>
        {s.connected && <button className="btn-outline" onClick={check} disabled={checking} data-testid="youtube-check-button">{checking ? "Checking…" : "Check connection"}</button>}
        {s.connected && <button className="btn-outline" onClick={disconnect} data-testid="youtube-disconnect-button">Disconnect</button>}
      </div>
    </div>
  );
};
